import { useState } from 'react';
import { motion } from 'framer-motion';

import { PhaseChoice, ReqTimer } from '@/api/generated/game';
import { selectAssignee, selectCanAct, useActions, useGame } from '@/store/game';

import { SeatPicker } from './SeatPicker';
import { TimerOverlay } from './TimerOverlay';

/**
 * Что можно сделать с карточкой на столе. Живёт отдельно от разметки самой
 * карточки, потому что карточек две: обычная (04) и секретная (06), а действия
 * над ними одни и те же.
 *
 * Кто именно вправе нажимать, решает сервер; здесь только то, что видно из
 * состояния: исполнитель (`pending.assigneeSeat`), остаток спасателя и стадия.
 * Локальное состояние сбрасывается ключом по карточке — вызывающий монтирует
 * компонент заново на каждую новую.
 */
export function CardActions() {
  const state = useGame((s) => s.state);
  const assignee = useGame(selectAssignee);
  const canAct = useGame(selectCanAct);
  const { complete, skip, useRescuer, pickTarget } = useActions();

  const [rescuing, setRescuing] = useState(false);
  const [timing, setTiming] = useState(false);

  const pending = state?.pending;
  if (!state || !pending) return null;

  const card = pending.card;
  const assigneeName = assignee?.name ?? 'игрока';
  const choosing = state.phase === PhaseChoice;

  // Спасателя играет исполнитель, один раз на карточку и только если он у него
  // остался; жертвой может стать любой, кроме него самого, у кого не кончились
  // «жертвы» (../clown-back/internal/domain/game/reducer.go, decideUseRescuer).
  const rescuerLeft = (assignee?.rescuers ?? 0) > 0 && pending.rescued !== true;
  const victims = state.players.filter((p) => p.seat !== pending.assigneeSeat && p.victims > 0);

  // `duration` без требования таймера тоже считается: это длительность задания,
  // и отмерять её нечем, кроме него.
  const timerSeconds =
    card && (card.duration != null || card.requires?.includes(ReqTimer))
      ? (card.duration ?? 60)
      : null;

  return (
    <motion.div
      className="mt-3"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay: 0.12 }}
    >
      {choosing ? (
        <SeatPicker
          title="Кому это задание"
          players={state.players.filter((p) => p.seat !== pending.assigneeSeat)}
          onPick={pickTarget}
        />
      ) : rescuing ? (
        <SeatPicker
          title="На кого перекинуть"
          players={victims}
          onPick={(seat) => {
            setRescuing(false);
            useRescuer(seat);
          }}
          onCancel={() => setRescuing(false)}
          empty="Перекидывать не на кого: жертвы кончились."
        />
      ) : (
        <div className="flex flex-col gap-rule border-2 border-line bg-line">
          {/* Реквизит за столом физический, кнопки он не запирает: таймер
              предлагается, а не требуется — карточку можно закрыть и без него. */}
          {timerSeconds != null && (
            <button
              type="button"
              onClick={() => setTiming(true)}
              className="flex h-tap items-center bg-panel px-4 text-left text-meta uppercase tracking-widest text-ink"
            >
              Таймер · {timerSeconds} с
            </button>
          )}

          <button
            type="button"
            onClick={complete}
            disabled={!canAct}
            className="flex h-act items-center gap-3 bg-panel px-4 text-left font-display text-row disabled:text-mute"
          >
            <span className="text-acc" aria-hidden>
              ✓
            </span>
            ВЫПОЛНИЛ
          </button>

          <div className="flex gap-rule">
            <button
              type="button"
              onClick={skip}
              disabled={!canAct}
              className="flex h-tap flex-1 items-center justify-center bg-panel px-4 text-meta uppercase tracking-widest text-mute disabled:opacity-40"
            >
              Пропустить
            </button>
            <button
              type="button"
              onClick={() => setRescuing(true)}
              disabled={!canAct || !rescuerLeft}
              className="flex h-tap flex-1 items-center justify-center bg-panel px-4 text-meta uppercase tracking-widest text-acc disabled:opacity-40"
            >
              Спасатель
            </button>
          </div>
        </div>
      )}

      {!canAct && <p className="mt-3 text-base text-mute">Задание у {assigneeName} — кнопки его.</p>}

      {timing && card && timerSeconds != null && (
        <TimerOverlay seconds={timerSeconds} text={card.text} onClose={() => setTiming(false)} />
      )}
    </motion.div>
  );
}
